import { useState } from "react";

import { useAuthContext } from "@eisox/auth";
import { Page, Select } from "@eisox/design-system";
import { RedirectionIcon } from "@eisox/icons";
import { createFileRoute } from "@tanstack/react-router";

import { BOILER_ROOM_MODULE_TYPES, getModuleLabel, getPLCPageLink } from "~/features/module";
import { requirePagePermission } from "~/helpers/auth/route-permission.helper";
import { buildingPageHead } from "~/helpers/document-title.helper";
import { m } from "~/paraglide/messages";
import { useBuildingContext } from "~/providers/building.provider";

export const Route = createFileRoute("/_authenticated/buildings_/$buildingId/bms")({
  beforeLoad: ({ context: { permissions }, params: { buildingId }, preload }) => {
    requirePagePermission(permissions, "gtb", buildingId, preload);
  },
  head: ({ match: { context }, params: { buildingId } }) => buildingPageHead(context.queryClient, buildingId, m.bms()),
  component: RouteComponent,
});

function RouteComponent() {
  const { buildingId } = Route.useParams();

  const { token } = useAuthContext(Route.id);
  const { boilerRooms, airHandlerUnits, modules } = useBuildingContext(Route.id);

  // One entry per proxy-enabled boiler-room module — the GTB view is per PLC, not per boiler room
  // (a single module can own several boiler rooms).
  const selectableModules = modules.filter(md => BOILER_ROOM_MODULE_TYPES.includes(md.type) && md.proxy?.isEnabled);

  const initialModule = selectableModules.find(md => !md.proxy?.isExternal) ?? selectableModules[0];

  const [currentModuleId, setCurrentModuleId] = useState(initialModule?.id);
  const currentModule = modules.find(md => md.id === currentModuleId);

  const handleChangeModule = (value: string) => {
    const module = modules.find(md => md.id === value);
    // External modules have no embeddable view — open the PLC page directly in a new tab.
    if (module?.proxy?.isExternal && token) {
      window.open(getPLCPageLink(token, buildingId, value), "_blank", "noopener,noreferrer");
    }
    setCurrentModuleId(value);
  };

  return (
    <Page.Root>
      <Page.Header>
        <Page.Title>{m.bms()}</Page.Title>
        {selectableModules.length > 1 && (
          <Select.Root multiple={false} value={currentModuleId} onValueChange={handleChangeModule}>
            <Select.Trigger className="w-[200px]">
              <Select.Value />
            </Select.Trigger>
            <Select.Content>
              <Select.Viewport>
                {selectableModules.map(md => (
                  <Select.Item key={md.id} value={md.id}>
                    {getModuleLabel(md, boilerRooms, airHandlerUnits)}
                    {md.proxy?.isExternal && <RedirectionIcon />}
                  </Select.Item>
                ))}
              </Select.Viewport>
            </Select.Content>
          </Select.Root>
        )}
      </Page.Header>
      {currentModule?.proxy?.isEnabled && !currentModule.proxy.isExternal && token && (
        <Page.Content data-testid="BMSAirHandlerUnitSettings">
          <iframe
            className="bg-white h-full w-full overflow-hidden border-0"
            title="GTB viewer"
            src={getPLCPageLink(token, buildingId, currentModule.id)}
          />
        </Page.Content>
      )}
    </Page.Root>
  );
}
